import type { ReactNode } from 'react';
import type { ButtonProps } from '../Button/Button';

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { EMPTY_ARRAY } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import Button from '../Button/Button';
import { PopoverPanelPlacement, popoverPanelPlacementClasses } from './classes';

export type PopoverPlacementOptions = keyof typeof popoverPanelPlacementClasses;

interface PopoverComponentProps {
  /**
   * Popover button
   */
  popoverButton?: ReactNode;
  /**
   * Popover panel placement
   */
  popoverPanelPlacement?: PopoverPlacementOptions;
  /**
   * Popover panel placement for phones
   */
  popoverPhonePanelPlacement?: PopoverPlacementOptions;
  /**
   * Popover content
   */
  content: ReactNode;
  /**
   * Popover button props
   */
  popoverButtonProps?: Omit<ButtonProps, 'ref'> & {
    isOurButton?: boolean;
  };
  /**
   * Additional CSS classes for component wrapper
   */
  additionalClasses?: string;
  /**
   * Additional CSS classes for popover panel
   */
  additionalPopoverPanelClasses?: string;
  /**
   * Component test id
   */
  testId?: string;
}

const PopoverComponent = ({
  popoverButton,
  popoverPanelPlacement = PopoverPanelPlacement.RightBottom,
  popoverPhonePanelPlacement = PopoverPanelPlacement.TopRight,
  content = EMPTY_ARRAY,
  additionalClasses = '',
  popoverButtonProps,
  additionalPopoverPanelClasses = '',
  testId,
}: PopoverComponentProps) => {
  const basePanelPlacementClasses = useMemo(
    () =>
      popoverPanelPlacementClasses[popoverPanelPlacement]
        .split(' ')
        .map((el) => `lg:${el}`)
        .join(' '),
    [popoverPanelPlacement],
  );

  const popoverButtonBaseProps = useMemo(() => {
    const { isOurButton, ...rest } = popoverButtonProps || {};
    return {
      ...rest,
      ...(isOurButton ? { as: Button } : {}),
    };
  }, [popoverButtonProps]);

  return (
    <Popover
      className={twMerge('relative w-fit z-20', additionalClasses)}
      {...getTestProps(testId, 'container')}
    >
      <PopoverButton
        className="focus:outline-hidden focus-visible:outline-hidden cursor-pointer"
        {...getTestProps(testId, 'button')}
        {...popoverButtonBaseProps}
      >
        {popoverButton}
      </PopoverButton>
      <PopoverPanel
        className={twMerge(
          'px-0 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-200 shadow-lg rounded-lg',
          'absolute border-2 border-slate-200 dark:border-slate-700 overflow-visible',
          'ease-out duration-200 data-closed:opacity-0 data-closed:translate-y-1',
          basePanelPlacementClasses,
          popoverPanelPlacementClasses[popoverPhonePanelPlacement],
          additionalPopoverPanelClasses,
        )}
        transition
        {...getTestProps(testId, 'popover')}
      >
        {content}
      </PopoverPanel>
    </Popover>
  );
};

export default PopoverComponent;
